import React, { useMemo, useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { 
  ColDef, 
  ColGroupDef,
  ModuleRegistry,
  AllCommunityModule 
} from "ag-grid-community";
import ExportButton from "../pages/Exportbutton";

ModuleRegistry.registerModules([AllCommunityModule]);

export interface BranchWiseMonthRow {
  Month: string;
  Bold: number;
  CYAmount: number;
  LYAmount: number;
  GrowthPer: number;
}

export interface BranchWiseRow {
  Particular: string;
  DataM: BranchWiseMonthRow[];
}

interface BranchWiseTableProps {
  heading: string;
  rowData: BranchWiseRow[];
  yr1Label?: string; 
  yr2Label?: string; 
}

type FilterMode = "all" | "monthly" | "quarterly";

const formatAmount = (val: number) =>
  Number(val || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatGrowth = (val: number) => {
  const num = Number(val || 0);
  return `${num > 0 ? "+" : ""}${num.toFixed(2)}%`;
};

// ── BRANCH TOTAL CARD ────────────────────────────────────────────────────────
interface BranchTotalCardProps {
  row: BranchWiseRow;
  yr1Label: string;
  yr2Label: string;
}

const BranchTotalCard: React.FC<BranchTotalCardProps> = ({ row, yr1Label, yr2Label }) => {
  // Sum only non-quarter months (idx 0,1,2,4,5,6,8,9,10,12,13,14)
  const quarterIndices = new Set([3, 7, 11, 15]);
  let lyTotal = 0, cyTotal = 0;
  row.DataM?.forEach((m, idx) => {
    if (!quarterIndices.has(idx)) {
      lyTotal += m.LYAmount || 0;
      cyTotal += m.CYAmount || 0;
    }
  });
  const growth = lyTotal !== 0 ? ((cyTotal - lyTotal) / Math.abs(lyTotal)) * 100 : cyTotal !== 0 ? 100 : 0;
  const growthColor = growth > 0 ? "#16a34a" : growth < 0 ? "#dc2626" : "#94a3b8";
  const growthBg = growth > 0 ? "#dcfce7" : growth < 0 ? "#fee2e2" : "#f1f5f9";

  return (
    <div className="br-branch-total-card">
      <div className="br-branch-total-header">
        <span className="br-branch-total-icon">Σ</span>
        <span className="br-branch-total-label">Annual Total — {row.Particular}</span>
      </div>
      <div className="br-branch-total-body">
        <div className="br-branch-total-row">
          <span>FY {yr1Label}</span>
          <span>{formatAmount(lyTotal)}</span>
        </div>
        <div className="br-branch-total-row">
          <span>FY {yr2Label}</span>
          <span>{formatAmount(cyTotal)}</span>
        </div>
        <div className="br-branch-total-growth" style={{ backgroundColor: growthBg, color: growthColor }}>
          <span style={{ color: "#475569", fontWeight: 600 }}>Annual Growth</span>
          <span style={{ fontWeight: 800 }}>{formatGrowth(growth)}</span>
        </div>
      </div>
    </div>
  );
};

// ── MOBILE CARD ──────────────────────────────────────────────────────────────
interface MobileCardProps {
  row: BranchWiseRow;
  visibleMonths: { name: string; isQ: boolean; originalIndex: number }[];
  yr1Label: string;
  yr2Label: string;
}

const BranchMobileCard: React.FC<MobileCardProps> = ({ row, visibleMonths, yr1Label, yr2Label }) => {
  return (
    <>
      <div className="br-mobile-card">
        <div className="br-card-title">{row.Particular}</div>
        <div className="br-card-body-grid-wrapper">
          {visibleMonths.map((m) => {
            const mData = row.DataM?.[m.originalIndex];
            if (!mData) return null;

            const isQuarter = m.isQ;
            const growth = mData.GrowthPer || 0;
            const growthColor = growth > 0 ? "#16a34a" : growth < 0 ? "#dc2626" : "#94a3b8";
            const growthBg = growth > 0 ? "#dcfce7" : growth < 0 ? "#fee2e2" : "#f1f5f9";

            return (
              <div
                key={m.originalIndex}
                className={`br-card-month-block ${isQuarter ? "br-card-quarter-bg" : "br-card-month-bg"}`}
              >
                <div className="br-card-month-header">
                  <span>{m.name}</span>
                  {isQuarter && <span className="br-quarter-badge">Q</span>}
                </div>
                <div className="br-card-data-row">
                  <span className="br-lbl">FY {yr1Label}</span>
                  <span className="br-val">{formatAmount(mData.LYAmount)}</span>
                </div>
                <div className="br-card-data-row">
                  <span className="br-lbl">FY {yr2Label}</span>
                  <span className="br-val cy-val">{formatAmount(mData.CYAmount)}</span>
                </div>
                <div className="br-card-data-row br-growth-pill" style={{ backgroundColor: growthBg, color: growthColor }}>
                  <span className="br-lbl" style={{ color: "#475569", fontWeight: 600 }}>Growth</span>
                  <span className="br-val" style={{ fontWeight: 700 }}>{formatGrowth(growth)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Branch-wise total card after each particular */}
      <BranchTotalCard row={row} yr1Label={yr1Label} yr2Label={yr2Label} />
    </>
  );
};

// ── CORE COMPONENT ───────────────────────────────────────────────────────────
const BranchWiseAgGridTable: React.FC<BranchWiseTableProps> = ({
  heading,
  rowData = [],
  yr1Label = "Prev Year",
  yr2Label = "Curr Year",
}) => {
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [isGridReady, setIsGridReady] = useState(false);

  useEffect(() => {
    setIsGridReady(false); 
    const timer = setTimeout(() => setIsGridReady(true), 40);
    return () => clearTimeout(timer);
  }, [heading]);
  // }, [heading, rowData.length]);

  const staticMonthsTemplate = useMemo(() => [
    { name: "April", isQ: false, idx: 0 }, { name: "May", isQ: false, idx: 1 }, { name: "June", isQ: false, idx: 2 }, { name: "Quarter 1", isQ: true, idx: 3 },
    { name: "July", isQ: false, idx: 4 }, { name: "August", isQ: false, idx: 5 }, { name: "September", isQ: false, idx: 6 }, { name: "Quarter 2", isQ: true, idx: 7 },
    { name: "October", isQ: false, idx: 8 }, { name: "November", isQ: false, idx: 9 }, { name: "December", isQ: false, idx: 10 }, { name: "Quarter 3", isQ: true, idx: 11 },
    { name: "January", isQ: false, idx: 12 }, { name: "February", isQ: false, idx: 13 }, { name: "March", isQ: false, idx: 14 }, { name: "Quarter 4", isQ: true, idx: 15 }
  ], []);

  const visibleMonthsList = useMemo(() => {
    const list = staticMonthsTemplate.map(m => ({ name: m.name, isQ: m.isQ, originalIndex: m.idx }));
    if (filterMode === "monthly") return list.filter((m) => !m.isQ);
    if (filterMode === "quarterly") return list.filter((m) => m.isQ);
    return list;
  }, [filterMode, staticMonthsTemplate]);

  const hasMonths   = staticMonthsTemplate.some((m) => !m.isQ);
  const hasQuarters = staticMonthsTemplate.some((m) =>  m.isQ);

  // Compute branch-wise annual total (sum of non-quarter months)
  const quarterIndices = new Set([3, 7, 11, 15]);
 
  const columnDefs = useMemo(() => {
    const baseDefs: (ColDef | ColGroupDef)[] = [
      {
        field: "Particular",
        headerName: "Particular",
        pinned: "left",
        width: 180,
        cellClass: "br-branch-pinned-column",
        headerClass: "br-branch-pinned-header-cell",
        cellStyle: { fontWeight: "600", color: "#1e293b", background: "#f8fafc", textAlign: "left" }
      }
    ];

    staticMonthsTemplate.forEach((m) => {
      if (filterMode === "monthly" && m.isQ) return;
      if (filterMode === "quarterly" && !m.isQ) return;

      const groupSuffixClass = m.isQ ? "quarter-group-header br-month-block-border" : "month-group-header br-month-block-border";
      const keyStr = m.name.toLowerCase().replace(/\s+/g, "_");

      baseDefs.push({
        groupId: `bw_mat_root_group_${heading.toLowerCase()}_${keyStr}`,
        headerName: m.name, 
        headerClass: `${groupSuffixClass} br-centered-header-group`,
        children: [
          {
            colId: `bw_sub_ly_${heading.toLowerCase()}_${keyStr}`,
            headerName: yr1Label, 
            width: 110,
            suppressMovable: true,
            headerClass: "br-centered-sub-label",
            valueGetter: (params) => params.data?.DataM?.[m.idx]?.LYAmount,
            valueFormatter: (p) => formatAmount(p.value),
            cellStyle: () => ({
              textAlign: "right", 
              fontWeight: m.isQ ? "700" : "400",
              background: m.isQ ? "#f1f5f9" : "transparent"
            })
          },
          {
            colId: `bw_sub_cy_${heading.toLowerCase()}_${keyStr}`,
            headerName: yr2Label, 
            width: 110,
            suppressMovable: true,
            headerClass: "br-centered-sub-label",
            valueGetter: (params) => params.data?.DataM?.[m.idx]?.CYAmount,
            valueFormatter: (p) => formatAmount(p.value),
            cellStyle: () => ({
              textAlign: "right", 
              fontWeight: m.isQ ? "700" : "400",
              background: m.isQ ? "#f1f5f9" : "transparent"
            })
          },
          {
            colId: `bw_sub_gr_${heading.toLowerCase()}_${keyStr}`,
            headerName: "Growth %", 
            width: 105,
            suppressMovable: true,
            headerClass: "br-centered-sub-label br-growth-header-label",
            cellClass: "br-month-block-border",
            valueGetter: (params) => params.data?.DataM?.[m.idx]?.GrowthPer,
            valueFormatter: (p) => formatGrowth(p.value),
            cellStyle: (p) => {
              const val = Number(p.value || 0);
              return {
                textAlign: "right", 
                fontWeight: "700",
                color: val > 0 ? "#059669" : val < 0 ? "#dc2626" : "#94a3b8",
                background: m.isQ ? "#e2e8f0" : "transparent"
              };
            }
          }
        ]
      });
    });

    // ── ANNUAL TOTAL COLUMN GROUP (branch-wise) ──
    baseDefs.push({
      groupId: `bw_annual_total_${heading.toLowerCase()}`,
      headerName: "Annual Total",
      headerClass: "annual-total-group-header br-centered-header-group",
      children: [
        {
          colId: `bw_annual_ly_${heading.toLowerCase()}`,
          headerName: yr1Label,
          width: 120,
          suppressMovable: true,
          headerClass: "br-centered-sub-label",
          valueGetter: (params) => {
            if (!params.data?.DataM) return 0;
            let ly = 0;
            params.data.DataM.forEach((m: BranchWiseMonthRow, idx: number) => {
              if (!quarterIndices.has(idx)) ly += m.LYAmount || 0;
            });
            return ly;
          },
          valueFormatter: (p) => formatAmount(p.value),
          cellStyle: () => ({ textAlign: "right", fontWeight: "700", background: "#fffbeb" })
        },
        {
          colId: `bw_annual_cy_${heading.toLowerCase()}`,
          headerName: yr2Label,
          width: 120,
          suppressMovable: true,
          headerClass: "br-centered-sub-label",
          valueGetter: (params) => {
            if (!params.data?.DataM) return 0;
            let cy = 0;
            params.data.DataM.forEach((m: BranchWiseMonthRow, idx: number) => {
              if (!quarterIndices.has(idx)) cy += m.CYAmount || 0;
            });
            return cy;
          },
          valueFormatter: (p) => formatAmount(p.value),
          cellStyle: () => ({ textAlign: "right", fontWeight: "700", background: "#fffbeb" })
        },
        {
          colId: `bw_annual_gr_${heading.toLowerCase()}`,
          headerName: "Growth %",
          width: 110,
          suppressMovable: true,
          headerClass: "br-centered-sub-label br-growth-header-label",
          cellClass: "br-month-block-border",
          valueGetter: (params) => {
            if (!params.data?.DataM) return 0;
            let ly = 0, cy = 0;
            params.data.DataM.forEach((m: BranchWiseMonthRow, idx: number) => {
              if (!quarterIndices.has(idx)) { ly += m.LYAmount || 0; cy += m.CYAmount || 0; }
            });
            return ly !== 0 ? ((cy - ly) / Math.abs(ly)) * 100 : cy !== 0 ? 100 : 0;
          },
          valueFormatter: (p) => formatGrowth(p.value),
          cellStyle: (p) => {
            const val = Number(p.value || 0);
            return {
              textAlign: "right",
              fontWeight: "800",
              color: val > 0 ? "#059669" : val < 0 ? "#dc2626" : "#94a3b8",
              background: "#fef9c3"
            };
          }
        }
      ]
    });

    return baseDefs;
  }, [filterMode, yr1Label, yr2Label, heading, staticMonthsTemplate]);

  const pinnedBottomRowData = useMemo(() => {
    if (!rowData || rowData.length === 0 || !rowData[0]?.DataM) return [];

    const totalRow: any = { Particular: "GRAND TOTAL", DataM: [] };

    staticMonthsTemplate.forEach((m) => {
      let totalLY = 0, totalCY = 0;
      rowData.forEach((branch) => {
        const mData = branch.DataM?.[m.idx];
        if (mData) { totalLY += mData.LYAmount || 0; totalCY += mData.CYAmount || 0; }
      });
      const totalGrowth = totalLY !== 0 ? ((totalCY - totalLY) / Math.abs(totalLY)) * 100 : totalCY !== 0 ? 100 : 0;
      totalRow.DataM.push({ Month: m.name, Bold: m.isQ ? 1 : 0, LYAmount: totalLY, CYAmount: totalCY, GrowthPer: totalGrowth });
    });

    return [totalRow];
  }, [rowData, staticMonthsTemplate]);

  const mobileGrandTotalSummary = useMemo(() => {
    let lySum = 0, cySum = 0;
    rowData.forEach(row => {
      row.DataM?.forEach((m, idx) => { if (!quarterIndices.has(idx)) { lySum += m.LYAmount || 0; cySum += m.CYAmount || 0; } });
    });
    const growth = lySum !== 0 ? ((cySum - lySum) / lySum) * 100 : 0;
    return { lySum, cySum, growth };
  }, [rowData]);

  return (
    <>
      <style>{`
        /* ── GRID THEME ── */
        .br-grid .ag-root-wrapper { border: 1px solid #cbd5e1 !important; border-radius: 8px !important; overflow: hidden; }
        .br-grid .ag-root { background-color: #ffffff !important; }
        .br-grid .ag-header { background-color: #1e293b !important; }
        .br-grid .ag-header-row { background-color: #1e293b !important; }
        .br-grid .br-centered-header-group .ag-header-group-cell-label { justify-content: center !important; text-align: center !important; width: 100%; }
        .br-grid .ag-header-group-cell { background-color: #0f4c81 !important; color: #ffffff !important; font-weight: 700 !important; font-size: 13.5px !important; border-bottom: 1px solid #1e293b !important; }
        .br-grid .br-centered-sub-label .ag-header-cell-label { justify-content: center !important; text-align: center !important; }
        .br-grid .ag-header-cell-text { color: #ffffff !important; font-weight: 600 !important; font-size: 12px !important; }
        .br-grid .br-branch-pinned-header-cell .ag-header-cell-label { justify-content: flex-start !important; }
        .br-grid .br-branch-pinned-column { border-right: 2px solid #475569 !important; box-shadow: 2px 0 5px rgba(0,0,0,0.04); }
        .br-grid .ag-header-cell[col-id="Particular"] { border-right: 2px solid #475569 !important; }
        .br-grid .quarter-group-header { background-color: #334155 !important; }
        .br-grid .annual-total-group-header { background-color: #92400e !important; }
        .br-grid .br-month-block-border { border-right: 1px solid #000000 !important; }
        .br-grid .ag-header-group-cell.br-month-block-border { border-right: 1px solid #000000 !important; }
        .br-grid .ag-header-cell.br-month-block-border { border-right: 1px solid #000000 !important; }
        .br-grid .ag-row { border-bottom: 1px solid #e2e8f0 !important; background-color: #ffffff; }
        .br-grid .ag-row-odd { background-color: #f8fafc !important; }
        .br-grid .ag-cell { display: flex; align-items: center; padding: 0 12px; border-right: 1px solid #e2e8f0 !important; justify-content: flex-end !important; }
        .br-grid .ag-cell[col-id="Particular"] { justify-content: flex-start !important; }
        .br-grid .ag-row:hover { background-color: #dbeafe !important; }
        .br-grid .ag-row-pinned { background-color: #0f172a !important; font-weight: 700 !important; }
        .br-grid .ag-row-pinned .ag-cell { background-color: #1e293b !important; color: #ffffff !important; border-top: 2px solid #475569 !important; }

        /* ── TOOLBAR ── */
        .br-toolbar {
          display: flex;
          align-items: center;
          padding: 14px 18px;
          background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0f4c81 100%);
          border-bottom: 1px solid #1e3a5f;
          flex-wrap: wrap;
          gap: 10px;
        }
        .br-toolbar-title {
          margin: 0;
          font-size: 15px;
          font-weight: 700;
          color: #f0f9ff;
          letter-spacing: 0.3px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .br-toolbar-title svg { filter: drop-shadow(0 0 4px rgba(96,165,250,0.6)); }
        .br-toolbar-title span { text-shadow: 0 1px 3px rgba(0,0,0,0.3); }
        .br-filter-group {
          display: flex;
          background: rgba(255,255,255,0.1);
          padding: 3px;
          border-radius: 9px;
          gap: 2px;
          border: 1px solid rgba(255,255,255,0.15);
          backdrop-filter: blur(4px);
        }
        .br-filter-btn {
          padding: 5px 12px;
          border: none;
          border-radius: 7px;
          cursor: pointer;
          font-size: 11px;
          letter-spacing: 0.5px;
          transition: all 0.15s ease;
          color: rgba(255,255,255,0.7);
          background: transparent;
        }
        .br-filter-btn:hover { background: rgba(255,255,255,0.15) !important; color: #fff !important; }
        .br-filter-btn.active {
          background: #ffffff !important;
          color: #0f4c81 !important;
          font-weight: 700 !important;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }

        /* ── MOBILE CARD VIEW ── */
        .br-desktop-view-container { display: block; }
        .br-mobile-cards-container { display: none; padding: 12px; background: linear-gradient(180deg, #f0f4ff 0%, #f8fafc 100%); }

        /* Month card - base */
        .br-card-month-bg {
          background: #ffffff !important;
          border: 1px solid #e2e8f0 !important;
          box-shadow: 0 1px 4px rgba(15,76,129,0.06);
          transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }
        .br-card-month-bg:hover {
          background: #eff6ff !important;
          border-color: #93c5fd !important;
          box-shadow: 0 4px 12px rgba(59,130,246,0.15) !important;
          transform: translateY(-2px);
          cursor: pointer;
        }

        /* Quarter card - distinct amber tint */
        .br-card-quarter-bg {
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%) !important;
          border: 1.5px solid #f59e0b !important;
          box-shadow: 0 2px 8px rgba(245,158,11,0.12);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .br-card-quarter-bg:hover {
          background: linear-gradient(135deg, #fef9c3 0%, #fde68a 100%) !important;
          box-shadow: 0 4px 14px rgba(245,158,11,0.25) !important;
          transform: translateY(-2px);
          cursor: pointer;
        }
        .br-card-quarter-bg .br-card-month-header { border-bottom-color: #f59e0b; color: #92400e; }

        .br-mobile-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 16px;
          margin-bottom: 8px;
          box-shadow: 0 4px 12px rgba(15,76,129,0.07);
          border-left: 5px solid #0f4c81;
          transition: box-shadow 0.15s ease, transform 0.15s ease;
        }
        .br-mobile-card:hover { box-shadow: 0 8px 20px rgba(15,76,129,0.13); transform: translateY(-1px); cursor: pointer; }
        .br-card-title { font-size: 15px; font-weight: 700; color: #0f172a; margin-bottom: 12px; border-bottom: 2px solid #e0e7ff; padding-bottom: 6px; }
        .br-card-body-grid-wrapper { display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-start; }
        .br-mobile-card .br-card-month-block { flex: 1 1 calc(50% - 10px); min-width: 140px; border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 5px; }
        .br-card-month-header { display: flex; justify-content: space-between; align-items: center; font-size: 11px; font-weight: 700; color: #334155; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; margin-bottom: 2px; text-transform: uppercase; }
        .br-quarter-badge { background: #b45309; color: #ffffff; font-size: 8px; padding: 1px 5px; border-radius: 3px; font-weight: 800; }
        .br-card-data-row { display: flex; justify-content: space-between; font-size: 12px; align-items: center; }
        .br-lbl { color: #64748b; font-weight: 500; }
        .br-val { color: #1e293b; font-weight: 600; text-align: right; }
        .cy-val { color: #0f4c81 !important; }
        .br-growth-pill { border-radius: 5px; padding: 3px 6px; margin-top: 3px; font-weight: 700; }

        /* Branch total card */
        .br-branch-total-card {
          background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%);
          border-radius: 10px;
          padding: 12px 16px;
          margin-bottom: 14px;
          border-left: 5px solid #f59e0b;
          box-shadow: 0 4px 12px rgba(15,23,42,0.2);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .br-branch-total-card:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(15,23,42,0.3); cursor: pointer; }
        .br-branch-total-header { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid rgba(255,255,255,0.12); }
        .br-branch-total-icon { font-size: 18px; color: #f59e0b; font-weight: 800; }
        .br-branch-total-label { font-size: 12px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
        .br-branch-total-body { display: flex; flex-direction: column; gap: 5px; }
        .br-branch-total-row { display: flex; justify-content: space-between; font-size: 13px; color: #e2e8f0; font-weight: 500; }
        .br-branch-total-growth { display: flex; justify-content: space-between; font-size: 13px; border-radius: 6px; padding: 5px 8px; margin-top: 4px; font-weight: 700; }

        .br-mobile-total-summary-card { background: #1e293b; color: #ffffff; border-radius: 12px; padding: 16px; margin-top: 10px; box-shadow: 0 4px 10px rgba(0,0,0,0.1); }
        .br-mobile-total-title { font-size: 11px; font-weight: 700; letter-spacing: 0.5px; color: #94a3b8; margin-bottom: 8px; text-transform: uppercase; }
        .br-mobile-total-row { display: flex; justify-content: space-between; font-size: 13.5px; margin-bottom: 4px; }

        @media (max-width: 768px) {
          .br-desktop-view-container { display: none !important; }
          .br-mobile-cards-container { display: block !important; }
        }
        @media (max-width: 480px) {
          .br-mobile-card .br-card-month-block { flex: 1 1 100%; }
        }

        @media (hover: hover) {
  .br-card-month-bg:hover {
    background: #eff6ff !important;
    border-color: #93c5fd !important;
    box-shadow: 0 4px 12px rgba(59,130,246,0.15) !important;
    transform: translateY(-2px);
    cursor: pointer;
  }

  .br-card-quarter-bg:hover {
    background: linear-gradient(135deg, #fef9c3 0%, #fde68a 100%) !important;
    box-shadow: 0 4px 14px rgba(245,158,11,0.25) !important;
    transform: translateY(-2px);
    cursor: pointer;
  }

  .br-mobile-card:hover {
    box-shadow: 0 8px 20px rgba(15,76,129,0.13);
    transform: translateY(-1px);
    cursor: pointer;
  }

  .br-branch-total-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(15,23,42,0.3);
    cursor: pointer;
  }
}
      `}</style>

      <div style={{ background: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
        
        {/* ── TOOLBAR ── */}
        <div className="br-toolbar">
          <h3 className="br-toolbar-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            <span>{heading}</span>
          </h3>

          {hasMonths && hasQuarters && (
            <div className="br-filter-group">
              {(["all", "monthly", "quarterly"] as FilterMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setFilterMode(mode)}
                  className={`br-filter-btn${filterMode === mode ? " active" : ""}`}
                >
                  {mode.toUpperCase()}
                </button>
              ))}
            </div>
          )}
          <span>
            <ExportButton variant="toolbar" params={{ type:"matrix", heading, rows:rowData, yr1Label, yr2Label, filterMode }} />
          </span>
        </div>

        {/* ── DESKTOP GRID VIEW ── */}
        <div className="br-desktop-view-container">
          <div className="br-grid" style={{ height: "500px", width: "100%", padding: "10px" }}>
            {isGridReady ? (
              <AgGridReact
                key={`matrix-group-grid-final-v34-${heading.toLowerCase()}-${filterMode}-${rowData?.length}`}
                rowData={rowData}
                columnDefs={columnDefs}
                theme="legacy"
                headerHeight={32}
                groupHeaderHeight={34}
                rowHeight={38}
                suppressMovableColumns={true}
                suppressColumnVirtualisation={true}
                defaultColDef={{
                  sortable: true,
                  resizable: true,
                  suppressHeaderMenuButton: true
                }}
                pinnedBottomRowData={pinnedBottomRowData}
                getRowStyle={(params) => {
                  if (params.node.rowPinned === "bottom") {
                    return { fontWeight: "700", background: "#1e293b" };
                  }
                  return undefined;
                }}
              />
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: "400px" }}>
                <div className="spinner-border text-primary opacity-50 mb-2" role="status">
                  <span className="visually-hidden">Syncing Structure...</span>
                </div>
                <p className="text-muted small fw-medium m-0">Aligning Matrix Columns...</p>
              </div>
            )}
          </div>
        </div>

        {/* ── MOBILE CANVAS ── */}
        <div className="br-mobile-cards-container">
          {rowData.map((row, index) => (
            <BranchMobileCard 
              key={index} 
              row={row} 
              visibleMonths={visibleMonthsList} 
              yr1Label={yr1Label} 
              yr2Label={yr2Label} 
            />
          ))}

          {/* Grand Total Summary */}
          <div className="br-mobile-total-summary-card">
            <div className="br-mobile-total-title">GRAND TOTAL SUMMARY</div>
            <div className="br-mobile-total-row">
              <span>FY {yr1Label}:</span>
              <span style={{ fontWeight: 700 }}>{formatAmount(mobileGrandTotalSummary.lySum)}</span>
            </div>
            <div className="br-mobile-total-row">
              <span>FY {yr2Label}:</span>
              <span style={{ fontWeight: 700 }}>{formatAmount(mobileGrandTotalSummary.cySum)}</span>
            </div>
            <div className="br-mobile-total-row" style={{ borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: "8px", marginTop: "6px" }}>
              <span style={{ fontWeight: 600 }}>Total Growth %:</span>
              <span style={{ 
                fontWeight: 800, 
                color: mobileGrandTotalSummary.growth > 0 ? "#4ade80" : mobileGrandTotalSummary.growth < 0 ? "#f87171" : "#cbd5e1" 
              }}>
                {formatGrowth(mobileGrandTotalSummary.growth)}
              </span>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default BranchWiseAgGridTable;

